import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import {
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  QuickFilterTrigger,
  ToolbarButton,
  useGridApiContext,
  useGridRootProps,
} from "@mui/x-data-grid";

const QuickFilterRoot = styled("div")({
  display: "grid",
  alignItems: "center",
});

const QuickFilterTriggerButton = styled(ToolbarButton, {
  shouldForwardProp: (prop) => prop !== "expanded",
})(({ expanded }) => ({
  gridArea: "1 / 1",
  width: "min-content",
  height: "min-content",
  zIndex: 1,
  opacity: expanded ? 0 : 1,
  pointerEvents: expanded ? "none" : "auto",
  transition: "opacity 150ms",
}));

/**
 * Toolbar quick filter using MUI QuickFilter components (replaces deprecated GridToolbarQuickFilter).
 *
 * @param {Object} props
 * @param {number} [props.debounceMs]
 * @param {Function} [props.quickFilterParser]
 * @param {Function} [props.quickFilterFormatter]
 * @param {string} [props.className]
 * @param {Object} [props.slotProps]
 */
function AsyncDataGridQuickFilter({
  debounceMs,
  quickFilterParser,
  quickFilterFormatter,
  className,
  slotProps,
  ...other
}) {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const Tooltip = rootProps.slots.baseTooltip;
  const QuickFilterIcon = rootProps.slots.quickFilterIcon;
  const QuickFilterClearIcon = rootProps.slots.quickFilterClearIcon;
  const BaseTextField = rootProps.slots.baseTextField;
  const BaseIconButton = rootProps.slots.baseIconButton;

  return (
    <QuickFilter
      debounceMs={debounceMs}
      parser={quickFilterParser}
      formatter={quickFilterFormatter}
      render={(quickFilterProps, state) => (
        <QuickFilterRoot {...quickFilterProps} className={className}>
          <Tooltip title={apiRef.current.getLocaleText("toolbarQuickFilterLabel")} enterDelay={0}>
            <QuickFilterTrigger
              render={(triggerProps) => (
                <QuickFilterTriggerButton
                  {...triggerProps}
                  expanded={state.expanded}
                  color="default"
                  aria-disabled={state.expanded}>
                  <QuickFilterIcon fontSize="small" />
                </QuickFilterTriggerButton>
              )}
            />
          </Tooltip>
          <QuickFilterControl
            render={({ ref, slotProps: controlSlotProps, ...controlProps }) => (
              <BaseTextField
                {...rootProps.slotProps?.baseTextField}
                {...controlProps}
                {...other}
                {...slotProps?.root}
                inputRef={ref}
                size="small"
                aria-label={apiRef.current.getLocaleText("toolbarQuickFilterLabel")}
                placeholder={apiRef.current.getLocaleText("toolbarQuickFilterPlaceholder")}
                slotProps={{
                  input: {
                    startAdornment: <QuickFilterIcon fontSize="small" />,
                    endAdornment: controlProps.value ? (
                      <QuickFilterClear
                        render={
                          <BaseIconButton
                            size="small"
                            edge="end"
                            aria-label={apiRef.current.getLocaleText("toolbarQuickFilterDeleteIconLabel")}>
                            <QuickFilterClearIcon fontSize="small" />
                          </BaseIconButton>
                        }
                      />
                    ) : null,
                    ...controlSlotProps?.input,
                  },
                  ...controlSlotProps,
                }}
                sx={{
                  gridArea: "1 / 1",
                  overflowX: "clip",
                  width: state.expanded ? 260 : "var(--trigger-width)",
                  opacity: state.expanded ? 1 : 0,
                  transition: "width 150ms, opacity 150ms",
                }}
              />
            )}
          />
        </QuickFilterRoot>
      )}
    />
  );
}

AsyncDataGridQuickFilter.propTypes = {
  debounceMs: PropTypes.number,
  quickFilterParser: PropTypes.func,
  quickFilterFormatter: PropTypes.func,
  className: PropTypes.string,
  slotProps: PropTypes.object,
};

export default AsyncDataGridQuickFilter;
